import { useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { useAssignRef } from "shared/lib/useAssignRef"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { Action } from "shared/types/Action"
import { FormRef } from "shared/types/ReactHookForm"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  UseRoomActionsArgs,
  useRoomActions
} from "features/room-management/model/useRoomActions"
import {
  RoomFormData,
  RoomFormState
} from "features/room-management/types/RoomFormData"
import { RoomFormModel } from "features/room-management/types/RoomFormModel"

import { ProjectPageRoomSidebarProps } from "../ProjectPageRoomSidebar"
import { ProjectPageRoomSidebarViewModel } from "../ProjectPageRoomSidebarView"

export function useProjectPageRoomSidebarViewModel({
  id,
  room,
  roomFormModel,
  callbacks,
  isDisabled,
  isPending,
  productTotal,
  price,
  isLoadingPrice,
  hideControls,
  isEditing,
  enterEditing,
  exitEditing,
  sidebarActiveTab,
  setSidebarActiveTab
}: ProjectPageRoomSidebarProps): ProjectPageRoomSidebarViewModel {
  const { t } = useTranslation()

  const formRef: FormRef<RoomFormState, unknown, RoomFormData> = useRef(null)
  useAssignRef({ ref: roomFormModel.formRef, value: formRef.current })

  const submit = useEvent((formData: RoomFormData) =>
    roomFormModel.onSubmit?.(formData)
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<RoomFormData>({
    onSubmit: submit,
    onSuccess: exitEditing
  })

  const isControlsDisabled = isPending || isDisabled

  const actionsArgs: UseRoomActionsArgs = {
    ...callbacks,
    idPrefix: "project-sidebar",
    onEdit: enterEditing,
    isEditDisabled: isControlsDisabled,
    isDuplicateDisabled: isControlsDisabled,
    isDeleteDisabled: isControlsDisabled,
    isCopyDisabled: isControlsDisabled,
    onAssignAttachments: undefined,
    onAddClarificationRequest: undefined
  }

  const { actions: normalActions } = useRoomActions(actionsArgs)

  const editingActions = useMemo<Action[]>(
    () => [
      {
        id: "room__edit__cancel",
        "data-testid": "room__edit__cancel",
        onClick: exitEditing,
        icon: IconName.CLOSE_CIRCLE,
        label: t("General.Cancel")
      },
      {
        id: "room__edit__submit",
        "data-testid": "room__edit__submit",
        type: "submit",
        form: id,
        icon: IconName.CHECK_CIRCLE,
        label: t("General.Confirm"),
        disabled: isSubmitting
      }
    ],
    [exitEditing, id, isSubmitting, t]
  )

  const derivedFormModel = useMemo<RoomFormModel>(
    () => ({
      ...roomFormModel,
      onSubmit: handleSubmit,
      formRef
    }),
    [roomFormModel, handleSubmit, formRef]
  )

  return {
    id,
    actions: isEditing ? editingActions : normalActions,
    roomFormModel: derivedFormModel,
    isDisabled: isPending || isSubmitting,
    isReadonly: !isEditing,
    hideControls,
    price,
    productTotal,
    isLoadingPrice,
    room,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
